import { useEffect, useRef, useState } from "react";
import apiClient from "../../core/config/api";
import "./pricing.css";
import { useNavigate } from "react-router-dom";
import TopUpCreditsModal from "../../components/TopUpCreditsModal";

type Plan = {
    id: number;
    name: string;
    description?: string | null;
    features?: Record<string, any> | null;
    base_credits: number;
    credit_notes: string | null;
    price: number;
    currency: string;
    billing_interval: string;
};

const STANDARD_INTERVALS = ["monthly", "yearly"];

const formatBillingInterval = (interval: string) => {
    const val = interval.toLowerCase();
    if (val === "yearly") return "year";
    if (val === "monthly") return "month";
    return interval.replace("_", " ");
};

// API rate limit shown on each plan card, by plan name (from the design).
const API_RATE_LIMITS: Record<string, { title: string; value: string }> = {
    free: { title: "API Rate Limit", value: "3 Requests/Minute (RPM)" },
    starter: { title: "API Rate Limit", value: "10 Requests/Minute (RPM)" },
    growth: { title: "API Rate Limit", value: "30 Requests/Minute (RPM)" },
    pro: { title: "API Rate Limit", value: "100 Requests/Minute (RPM)" },
    enterprise: { title: "Custom API Rate Limits", value: "(250–1,000+ RPM)" },
};

const CATCH_ALL_FEATURE = "Catch-All Resolver";
const API_FEATURE = "Email Verification API";

const Pricing = () => {
    const [activeTab, setActiveTab] = useState<"annual" | "monthly" | "payg">("annual");
    // The user's current plan, for the Pay As You Go panel.
    const [currentSub, setCurrentSub] = useState<{
        planName: string | null;
        interval: string | null;
        status: string | null;
        renewsOn: string | null;
        isFree: boolean;
    } | null>(null);
    const [subLoaded, setSubLoaded] = useState(false);
    // Pay As You Go: the Get top-up credits popup.
    const [showTopUp, setShowTopUp] = useState(false);
    // Pay As You Go is for free-trial and Pay As You Go users; paid
    // subscribers (a plan with a price) don't see the tab.
    const showPaygTab = !currentSub || currentSub.isFree;
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [buyingId, setBuyingId] = useState<number | null>(null);
    // The plan the user is currently subscribed to (null = none / not loaded yet).
    const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);

    const [showContactModal, setShowContactModal] = useState(false);
    const [contactForm, setContactForm] = useState({ full_name: "", phone: "", email: "", details: "" });
    const [contactSubmitting, setContactSubmitting] = useState(false);
    const [contactSuccess, setContactSuccess] = useState("");
    const [contactError, setContactError] = useState("");
    const [userProfile, setUserProfile] = useState<{ first_name: string; last_name?: string | null; email: string; phone_number?: string | null } | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setLoading(true);
                setError("");
                const res = await apiClient.get("/pricing/plans", { params: { is_active: true } });
                const rawPlans = Array.isArray(res.data) ? res.data : [];
                setPlans(
                    rawPlans.map((p: any) => {
                        const creditNotesFromFeatures =
                            p.features && typeof p.features === "object" && "_credit_notes" in p.features
                                ? String(p.features._credit_notes)
                                : null;
                        return {
                            ...p,
                            base_credits: Number(p.base_credits) || 0,
                            price: Number(p.price) || 0,
                            credit_notes: p.credit_notes ?? creditNotesFromFeatures,
                        };
                    })
                );
            } catch (e: any) {
                setError(e?.response?.data?.detail || "Failed to load plans");
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    useEffect(() => {
        const fetchSubscription = async () => {
            try {
                const res = await apiClient.get("/stripe-payment/subscription");
                if (res.data?.has_subscription) {
                    setCurrentPlanId(res.data.plan_id ?? null);
                    setCurrentSub({
                        planName: res.data.plan_name || null,
                        interval: res.data.billing_interval || null,
                        status: (res.data.status || "").toLowerCase() || null,
                        renewsOn: res.data.next_renewal_date || null,
                        isFree: Number(res.data.price || 0) <= 0,
                    });
                }
            } catch {
                // Non-fatal: pricing still renders without current-plan marking.
            } finally {
                setSubLoaded(true);
            }
        };

        fetchSubscription();
    }, []);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await apiClient.get("/auth/current");
                setUserProfile(res.data);
            } catch {
                // Non-fatal
            }
        };
        fetchProfile();
    }, []);

    const formatPrice = (price: number, currency = "USD") =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency.toUpperCase(),
            minimumFractionDigits: price === 0 ? 0 : 2,
            maximumFractionDigits: price === 0 ? 0 : 2,
        }).format(price);

    const featuresList = (plan: Plan) => {
        if (!plan.features) return [];
        if (Array.isArray(plan.features)) return plan.features as string[];
        return Object.values(plan.features)
            .filter((v) => typeof v === "string") as string[];
    };

    const handleContactSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setContactSubmitting(true);
        setContactError("");
        setContactSuccess("");
        try {
            await apiClient.post("/pricing/contact", contactForm);
            setContactSuccess("Your inquiry has been submitted. We will get in touch with you soon.");
            setContactForm({ full_name: "", phone: "", email: "", details: "" });
        } catch (err: any) {
            setContactError(err?.response?.data?.detail || "Failed to submit. Please try again.");
        } finally {
            setContactSubmitting(false);
        }
    };

    const handleBuy = (plan: Plan) => {
        setBuyingId(plan.id);
        navigate("/payment-details", {
            state: {
                planId: plan.id,
                planName: plan.name,
                description: plan.description,
                price: plan.price,
                currency: plan.currency,
                interval: plan.billing_interval,
                base_credits: plan.base_credits,
                credit_notes: plan.credit_notes,
                features: plan.features,
            },
        });
    };

    const PlanCard = ({ plan }: { plan: Plan }) => {
        const isFree = plan.price === 0 && !plan.name.toLowerCase().includes('enterprise');
        // Every plan includes the Catch-All Resolver (shown first, marked New)
        // and the Email Verification API. Added here when the plan's own
        // feature list in re_plans doesn't already say so.
        const feats = (() => {
            const list = featuresList(plan);
            const has = (name: string) => list.some((f) => f.trim().toLowerCase() === name.toLowerCase());
            return [
                ...(has(CATCH_ALL_FEATURE) ? [] : [CATCH_ALL_FEATURE]),
                ...list,
                ...(has(API_FEATURE) ? [] : [API_FEATURE]),
            ];
        })();
        const isCustomInterval = !STANDARD_INTERVALS.includes(plan.billing_interval.toLowerCase());
        const isEnterprisePlan = !isFree && (plan.name.toLowerCase() === 'enterprise' || isCustomInterval);
        // Mark the user's active plan. Until the subscription loads (currentPlanId
        // null) fall back to treating Free as the current plan, as before.
        const isCurrent = currentPlanId != null ? plan.id === currentPlanId : isFree;

        const middleLine = (() => {
            if (isEnterprisePlan) return "Estimated Range";
            if (isFree) return `${plan.base_credits.toLocaleString()} credits`;
            return `${plan.base_credits.toLocaleString()} credits / month`;
        })();

        const bottomLine = (() => {
            if (isEnterprisePlan) return "~$0.0009 – $0.0011 per verification";
            if (isFree) return plan.credit_notes ?? "";
            return plan.credit_notes ?? "";
        })();

        return (
            <div className="plan-main-box" key={plan.id}>
                <div className="plan-top">
                    {/* Display name only - the stored plan stays "Free", which
                        checkout and user lookups match on. */}
                    <p className="free-trial">{isFree ? "30-Days Free Trial" : plan.name}</p>

                    <p className="doller-price">
                        {isEnterprisePlan
                            ? "Custom Pricing"
                            : formatPrice(isFree ? 0 : plan.price, plan.currency)}{" "}
                        {!isEnterprisePlan && !isFree && (
                            <span className="span-year">
                                / {formatBillingInterval(plan.billing_interval)}
                            </span>
                        )}
                    </p>

                    <p className="free-credits">{middleLine}</p>

                    {bottomLine && (
                        <p className="competitors">
                            {!isFree && (
                                <img src="/images/per-verification.png" alt="" className="pricing-point-icon" />
                            )}
                            {bottomLine}
                        </p>
                    )}

                    <div className="b-linear" />

                    {isFree ? (
                        <>
                            <p className="limits">Credits valid</p>
                            <p className="No-api-access">for 30 days</p>
                        </>
                    ) : (
                        <>
                            <p className="limits">Best for</p>
                            <p className="No-api-access">{plan.description || "—"}</p>
                        </>
                    )}

                    {(() => {
                        const key = isFree ? "free" : isEnterprisePlan ? "enterprise" : plan.name.trim().toLowerCase();
                        const limit = API_RATE_LIMITS[key];
                        return limit ? (
                            <div className="plan-rate-limit">
                                <p className="plan-rate-limit-title">{limit.title}</p>
                                <p className="plan-rate-limit-value">{limit.value}</p>
                            </div>
                        ) : null;
                    })()}
                </div>

                {feats.length > 0 && (
                    <>
                        <p className="features">Features</p>
                        {feats.map((item, idx) => (
                            <div className="plan-feature-item" key={idx}>
                                <span className="plan-feature-check">✓</span>
                                <span className="plan-feature-text">
                                    {item}
                                    {item === CATCH_ALL_FEATURE && <span className="plan-feature-new">New</span>}
                                </span>
                            </div>
                        ))}
                    </>
                )}

                <div className="plan-spacer" />

                {isCurrent ? (
                    <button type="button" className="current-plan" disabled>
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="7.5" cy="7.5" r="7.5" fill="#00C2C2" />
                            <path d="M4 7.5L6.5 10L11 5.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Current Plan
                    </button>
                ) : isFree && currentPlanId !== null ? (
                    <button type="button" className="not-available" disabled>
                        Not Available
                    </button>
                ) : isEnterprisePlan ? (
                    <button
                        type="button"
                        className="contact-sales"
                        onClick={() => {
                            setContactSuccess("");
                            setContactError("");
                            setContactForm({
                                full_name: userProfile
                                    ? `${userProfile.first_name}${userProfile.last_name ? " " + userProfile.last_name : ""}`
                                    : "",
                                email: userProfile?.email ?? "",
                                phone: userProfile?.phone_number ?? "",
                                details: "",
                            });
                            setShowContactModal(true);
                        }}
                    >
                        Contact Sales
                    </button>
                ) : (
                    <button
                        type="button"
                        className="buy-now-1"
                        disabled={buyingId === plan.id}
                        onClick={() => handleBuy(plan)}
                    >
                        {buyingId === plan.id ? "Processing..." : "Buy Now"}
                    </button>
                )}
            </div>
        );
    };

    const renderStandardPlans = (interval: string) => {
        const filtered = plans
            .filter((p) => (p.billing_interval || "").toLowerCase() === interval)
            .sort((a, b) => {
                if (a.name.toLowerCase() === 'free') return -1;
                if (b.name.toLowerCase() === 'free') return 1;
                if (a.name.toLowerCase() === 'enterprise') return 1;
                if (b.name.toLowerCase() === 'enterprise') return -1;
                return a.price - b.price;
            });

        if (loading) {
            return <p className="text-center">Loading plans...</p>;
        }

        if (error) {
            return <p className="text-center text-danger">{error}</p>;
        }

        if (!filtered.length) {
            return <p className="text-center">No plans available.</p>;
        }

        return (
            <div className="pricing-panel">
                <div className="plans-grid">
                    {filtered.map((plan) => (
                        <PlanCard plan={plan} key={plan.id} />
                    ))}
                </div>
            </div>
        );
    };



    return (
        <div className="pricing-page">
            {showContactModal && (
                <div
                    className="enterprise-modal-overlay"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowContactModal(false); }}
                >
                    <div className="enterprise-modal" ref={modalRef}>
                        <button
                            type="button"
                            className="enterprise-modal-close-icon"
                            aria-label="Close"
                            onClick={() => setShowContactModal(false)}
                        >
                            &times;
                        </button>
                        {!contactSuccess ? (
                            <>
                                <h2 className="enterprise-modal-title">
                                    Get In Touch <span>With Us</span>
                                </h2>
                                <p className="enterprise-modal-subtitle">We'd love to hear from you!</p>
                            </>
                        ) : (
                            <>
                                <h2 className="enterprise-modal-title">
                                    <span>Thank You !</span>
                                </h2>
                            </>
                        )}
                        {contactSuccess ? (
                            <div className="enterprise-modal-success">
                                <p style={{ fontSize: '18px', lineHeight: '1.5' }}>{contactSuccess}</p>
                                <button
                                    type="button"
                                    className="enterprise-modal-close-btn"
                                    onClick={() => setShowContactModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleContactSubmit} className="enterprise-modal-form">
                                <input
                                    type="text"
                                    placeholder="Full Name"
                                    value={contactForm.full_name}
                                    onChange={(e) => setContactForm((f) => ({ ...f, full_name: e.target.value }))}
                                    required
                                />
                                <input
                                    type="tel"
                                    placeholder="Phone Number"
                                    value={contactForm.phone}
                                    onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
                                    required
                                />
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={contactForm.email}
                                    onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                                    required
                                />
                                <textarea
                                    placeholder="Details (Optional)"
                                    value={contactForm.details}
                                    onChange={(e) => setContactForm((f) => ({ ...f, details: e.target.value }))}
                                    rows={4}
                                />
                                {contactError && <p className="enterprise-modal-error">{contactError}</p>}
                                <button type="submit" className="enterprise-modal-submit" disabled={contactSubmitting}>
                                    {contactSubmitting ? "Submitting..." : "Submit"}
                                </button>
                            </form>
                        )}
                    </div>
                </div >
            )}

            <div className="dashboard-page">
                <h1>Choose Your Plan</h1>
                <p>Get started fast with free integrations, real-time syncing, and precise results.</p>
            </div>

            <div className="pricing-container">
                <div className="pricing-tabs">
                    <button
                        className={`pricing-tab ${activeTab === "annual" ? "active" : ""}`}
                        onClick={() => setActiveTab("annual")}
                    >
                        Annual <span className="save-the">Save ~25%</span>
                    </button>

                    <button
                        className={`pricing-tab ${activeTab === "monthly" ? "active" : ""}`}
                        onClick={() => setActiveTab("monthly")}
                    >
                        Monthly
                    </button>

                    {showPaygTab && (
                        <button
                            className={`pricing-tab ${activeTab === "payg" ? "active" : ""}`}
                            onClick={() => setActiveTab("payg")}
                        >
                            Pay As You Go
                        </button>
                    )}
                </div>

                <div className="pricing-content">
                    {activeTab === "annual" && renderStandardPlans("yearly")}
                    {activeTab === "monthly" && renderStandardPlans("monthly")}
                    {activeTab === "payg" && showPaygTab && (
                        <div className="payg-panel">
                            <svg className="payg-icon" viewBox="0 0 32 32" fill="none" stroke="#0f6e8c" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M5 27h22M8 27v-7h4v7M14 27V15h4v12M20 27v-9h4v9" />
                                <path d="M7 13l7-6 5 4 7-6M22 5h4v4" />
                            </svg>
                            {(() => {
                                // Wording from the XD: the user's plan name, then the two lines.
                                if (!subLoaded) return <p className="payg-text">Loading your plan...</p>;
                                const planLabel = (currentSub?.planName || "Free").replace(/\s*plan$/i, "");
                                return (
                                    <>
                                        <h2 className="payg-title">You&apos;re on the {planLabel} plan</h2>
                                        <p className="payg-text">
                                            PAYG isn&apos;t available alongside a subscription. Buy top-up credits from your dashboard
                                            instead — they stack with {planLabel} and share its renewal date.
                                        </p>
                                        <button type="button" className="payg-btn" onClick={() => setShowTopUp(true)}>
                                            Buy Credits
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>
            {showTopUp && <TopUpCreditsModal onClose={() => setShowTopUp(false)} />}
        </div>
    );
};

export default Pricing;
